import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Plan, Program } from "@heraion/shared";

import { useUnsavedGuard } from "../primitives/useUnsavedGuard";
import { useToast } from "../primitives/Toast";
import { useUndoShortcuts } from "../primitives/useUndoShortcuts";
import type { LeaveGuard } from "../Targets/LeaveGuard";
import type { PersistedSnapshot, ProgramApiFetch } from "../ProgramEditor/types";
import { editorDraftKey } from "../ProgramEditor/editorDraftStore";
import { sameContent } from "../ProgramEditor/draftContent";
import { useDraftPersistence } from "../ProgramEditor/useDraftPersistence";
import {
  useResumeConflict,
  type StoredEditorDraft,
} from "../ProgramEditor/useDraftResume";
import type { EditorSaveState } from "../ProgramEditor/EditorSaveStatus";
import {
  useOpenRead,
  useProgramVersion,
  type ServerCopy,
} from "../ProgramEditor/useProgramVersion";
import { useComposer, type Composer } from "./useComposer";

// #76 - useComposer + the unsaved-changes guard over its draft, in one call:
// ProgramComposer and PlanComposer share it. The guard needs the composer's
// draft and the composer needs the guard's markSaved, so a ref closes the loop
// (read only at save time). The compared value is the draft PLUS scheduledFor
// (a Plan's date lives outside the Draft); `extraDirty` adds state outside
// both, e.g. a staged exercise form.
//
// #336 - and the work safety around that draft, in this order: the guard
// (dirty, baseline), the device draft that follows it (useDraftPersistence),
// the resume question (useResumeConflict), then the exit dialog's three
// choices, built here from the guard's `leave` and the device draft's
// flush/discard. The server is written ONLY by Save.
// S5 - and the program's version (useProgramVersion): the open read that
// arms If-Match, shared with the resume question, the read before a save
// with no version in hand (`verifyVersion`, through a ref: the composer is
// built before it), and «Modificato altrove».
export function useComposerGuard(
  args: Omit<Parameters<typeof useComposer>[0], "onPersisted" | "resumeFrom"> & {
    /** Reads the server's copy at resume, for a draft that has none from the
     *  host (useResumeConflict). Absent: that draft resumes without asking. */
    apiFetch?: ProgramApiFetch;
  },
  opts: {
    onLeave: () => void;
    registerBackGuard?: (guard: LeaveGuard) => void;
    extraDirty?: (composer: Composer) => boolean;
    /** Host's account-scoped prefix; null/absent: no device draft. */
    draftScope?: string | null;
    /** The device draft read before mount (useDraftResume). */
    stored?: StoredEditorDraft | null;
    /** The signed-in user: an empty assignee list is them (draftContent). */
    callerSub?: string | null;
    /** A program seeded by the host (ProgramComposerProps.creationSeed). */
    hostSeed?: boolean;
  },
) {
  const { t } = useTranslation();
  const stored = opts.stored ?? null;
  const persistedRef = useRef<((saved: PersistedSnapshot) => void) | undefined>(
    undefined,
  );
  const verifyRef = useRef<((id: string) => Promise<boolean>) | undefined>(undefined);
  const { apiFetch, ...composerArgs } = args;
  const composer = useComposer({
    ...composerArgs,
    resumeFrom: stored?.blob ?? null,
    onPersisted: (saved) => persistedRef.current?.(saved),
    verifyVersion: (id) => verifyRef.current?.(id) ?? Promise.resolve(true),
  });
  const single = args.mode === "single";
  const serverDoc = single ? args.plan : args.program;
  const extraDirty = opts.extraDirty?.(composer) ?? false;

  const guard = useUnsavedGuard({
    value: composer.snapshot,
    initialBaseline: stored?.blob.baseline,
    extraDirty,
    onLeave: opts.onLeave,
    registerBackGuard: opts.registerBackGuard,
  });
  persistedRef.current = guard.markSaved;

  const { snapshotOf, cellUnit } = composer;
  const callerSub = opts.callerSub ?? null;
  const same = useCallback(
    (a: PersistedSnapshot, b: PersistedSnapshot) =>
      sameContent(a, b, single, cellUnit, callerSub),
    [single, cellUnit, callerSub],
  );
  const readServer = useCallback(
    async (id: string): Promise<ServerCopy | null> => {
      if (!apiFetch) return null;
      const collection = single ? "/api/me/plans" : "/api/me/programs";
      let etag: string | null = null;
      const raw = await apiFetch<unknown>(`${collection}/${encodeURIComponent(id)}`, {
        onEtag: (e) => {
          etag = e;
        },
      });
      const doc = single ? Plan.safeParse(raw) : Program.safeParse(raw);
      return doc.success ? { snap: snapshotOf(doc.data), etag } : null;
    },
    [apiFetch, single, snapshotOf],
  );
  const reader = apiFetch ? readServer : null;
  const readOnce = useOpenRead(reader);
  const conflict = useResumeConflict({
    stored,
    server: serverDoc ? () => snapshotOf(serverDoc) : null,
    readServer: reader ? async (id) => (await readOnce(id))?.snap ?? null : null,
    sameContent: same,
    resetTo: composer.resetTo,
    // Either answer makes the server's copy the baseline. Its validator was
    // armed by the open read (useProgramVersion).
    markSaved: guard.markSaved,
    currentBaseline: guard.getBaseline,
  });
  const version = useProgramVersion({
    lockId: single ? null : composer.serverId,
    readOnce,
    readServer: reader,
    etag: composer.etag,
    sameContent: same,
    getBaseline: guard.getBaseline,
    edited: guard.valueDirty,
    conflict: { pending: conflict.pending, show: conflict.show },
    savedAt: composer.lastSave?.at,
    resetTo: composer.resetTo,
    markSaved: guard.markSaved,
    savedElsewhere: composer.savedElsewhere,
    closeSavedElsewhere: composer.closeSavedElsewhere,
    save: composer.save,
  });
  verifyRef.current = version.verify;

  const scope = opts.draftScope ?? null;
  // #368: a plan made from a seed keeps no device draft under «new»: it
  // would overwrite, or discard, the draft of another new plan kept there.
  // Once a save went out or was queued, its draft is filed under the id the
  // create sent (mintedId, now serverId), the plan's own: opening the plan
  // finds it. #375: before that, under its source (PlanSeed.source), where
  // «Plan again» on the same session or plan finds it and asks. The move to
  // the id removes the source's blob (useDraftPersistence). No source: none.
  // #472: a program the host seeded (the catalog's Edit) has no source.
  const seededNew =
    (single ? !!args.planSeed : opts.hostSeed === true) && composer.draftId === null;
  const draftKeyId = seededNew
    ? (composer.serverId ?? args.planSeed?.source ?? null)
    : composer.draftId;
  const storageKey =
    scope === null || (seededNew && draftKeyId === null)
      ? null
      : editorDraftKey(
          scope,
          single ? "plan" : "program",
          args.authoredAs ?? "athlete",
          draftKeyId,
        );
  const { draftBlob } = composer;
  const { getBaseline } = guard;
  // While the resume question is open the device draft is left as it is:
  // neither rewritten nor removed. While the server is still being read the
  // editor is live, so the draft keeps its key: edits go to the same blob,
  // against the same baseline (the guard's, still the blob's), and «Scarta»,
  // the exit Save and a create's key move all reach it. If the read opens
  // the question and the answer is the server's copy, the editor goes clean
  // and the blob goes.
  const persistence = useDraftPersistence({
    storageKey: conflict.open ? null : storageKey,
    // The value only: a staged exercise form alone is not a draft to keep.
    dirty: guard.valueDirty,
    snapshot: () => draftBlob(getBaseline() ?? composer.snapshot),
    revision: composer.snapshot,
    initialSavedAtMs: stored?.savedAtMs ?? null,
  });

  // The exit's three choices. Discard drops the device draft (the server
  // stays as it was); Keep leaves it, flushed; Save leaves only once the
  // write landed, and a failed Save stays with its error line. A staged
  // exercise form is not in the draft: with one, Save is not offered, and
  // Keep only when the draft itself has changes to keep.
  // Save removes the device draft itself before leaving: the host unmounts
  // the editor right away, before the effect that follows a clean editor
  // could remove it, and a blob left behind would come back on the next
  // open as a stale draft.
  const { leave, hideDialog } = guard;
  const exitSave = useRef(false);
  const dialogProps = {
    ...guard.dialogProps,
    onDiscard: () => {
      void persistence.discard();
      leave();
    },
    onKeepDraft:
      storageKey !== null && guard.valueDirty
        ? () => {
            void persistence.flush();
            leave();
          }
        : undefined,
    onSave: extraDirty
      ? undefined
      : () => {
          hideDialog();
          exitSave.current = true;
          void composer.save().then(async (ok) => {
            if (!ok) {
              exitSave.current = false;
              guard.dialogProps.onKeepEditing();
              return;
            }
            try {
              await persistence.discard();
            } finally {
              leave();
            }
          });
        },
  };

  // Program editor only: the Plan editor stages its exercise form outside the
  // draft, and an undo under an open form would desync the two.
  const { onUndo, onRedo } = composer.undoRedo;
  useUndoShortcuts(single ? noop : onUndo, single ? noop : onRedo);

  // The toast is for a Save that leaves the editor open: not for the exit
  // dialog's, which is closing it. A queued save says so, like the save line.
  const toast = useToast();
  const { show } = toast;
  const lastAt = composer.lastSave?.at;
  const lastOutcome = composer.lastSave?.outcome;
  useEffect(() => {
    if (lastAt === undefined || !args.keepOpen || exitSave.current) return;
    show(
      lastOutcome === "queued"
        ? t("program.savedQueuedStaysOpen", {
            defaultValue: "Queued: it goes out when the network is back.",
          })
        : t("program.savedStaysOpen", { defaultValue: "Saved. The editor stays open." }),
    );
  }, [lastAt, lastOutcome, args.keepOpen, show, t]);

  const saveState: EditorSaveState = {
    busy: composer.busy,
    dirty: guard.isDirty,
    lastSave: composer.lastSave,
    isCreate: composer.isCreate,
  };

  return {
    composer,
    guard: { ...guard, dialogProps },
    saveState,
    toast,
    conflict,
    savedElsewhere: version.dialog,
  };
}

const noop = () => undefined;
